import { type SignIn, type ExperienceSocialConnector, AgreeToTermsPolicy } from '@logto/schemas';
import { useContext, useState } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import IdentifierSignInForm from '@/components/IdentifierSignInForm';
import PasswordSignInForm from '@/components/PasswordSignInForm';
import SocialSignInList from '@/containers/SocialSignInList';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import { useIsAimsLayout } from '@/hooks/use-layout-mode';
import useTerms from '@/hooks/use-terms';

import useIdentifierSignInMethods from '../IdentifierSignIn/use-identifier-sign-in-methods';

import styles from './index.module.scss';

type Props = {
  readonly signInMethods: SignIn['methods'];
  readonly socialConnectors: ExperienceSocialConnector[];
};

const Main = ({ signInMethods, socialConnectors }: Props) => {
  const { agreeToTermsPolicy } = useTerms();
  const { isPasswordOnly } = useIdentifierSignInMethods();
  const { experienceSettings } = useContext(PageContext);
  const isApplicant =
    useIsAimsLayout() && experienceSettings?.branding.aimsAudience === 'applicant';
  const [isCodeSignIn, setIsCodeSignIn] = useState(false);
  // Applicants (admissions sign-in mockup): email or phone + password on one page, with "Send me a
  // code instead" when the tenant also allows code sign-in.
  const passwordMethods = signInMethods.every(({ password }) => password);
  const codeMethods = signInMethods.filter(({ verificationCode }) => verificationCode);

  if (signInMethods.length === 0 && socialConnectors.length > 0) {
    return (
      <>
        <SocialSignInList className={styles.main} socialConnectors={socialConnectors} />
        {
          /**
           * Display agreement checkbox when only social sign-in methods are available
           * and the user needs to agree to terms manually.
           */
          agreeToTermsPolicy === AgreeToTermsPolicy.Manual && (
            <TermsAndPrivacyCheckbox className={styles.checkbox} />
          )
        }
      </>
    );
  }

  if (isApplicant && isCodeSignIn && codeMethods.length > 0) {
    return (
      <IdentifierSignInForm
        className={styles.main}
        signInMethods={codeMethods.map((method) => ({ ...method, password: false }))}
      />
    );
  }

  if (isApplicant && signInMethods.length > 0 && passwordMethods) {
    return (
      <PasswordSignInForm
        className={styles.main}
        signInMethods={signInMethods.map(({ identifier }) => identifier)}
        onUseCode={
          codeMethods.length > 0
            ? () => {
                setIsCodeSignIn(true);
              }
            : undefined
        }
      />
    );
  }

  if (isPasswordOnly) {
    return (
      <PasswordSignInForm
        className={styles.main}
        signInMethods={signInMethods.map(({ identifier }) => identifier)}
      />
    );
  }

  if (signInMethods.length > 0) {
    return <IdentifierSignInForm className={styles.main} signInMethods={signInMethods} />;
  }

  return null;
};

export default Main;
